import { animated, useSpring, type SpringValues } from 'react-spring';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Tooltip } from '@mentimeter/ragnar-ui/tooltip';
import { CorrectnessIndicator } from '../components/CorrectnessIndicator';

const TEXT_MARGIN = 16;

// we need two separate properties as the `isCorrect` needs to stay the same while the `isVisible` can change to fade in/out
export interface CorrectnessIndicatorStatus {
  isVisible: boolean;
  isCorrect: boolean;
}

interface ScoreProps {
  x: number;
  y: number;
  barWidth: number;
  textColor: string;
  height: number;
  scoreDisplayText: string;
  disableAnimations: boolean;
  correctnessIndicatorStatus: CorrectnessIndicatorStatus;
}

export const Score = ({
  x,
  y,
  barWidth,
  textColor,
  height,
  scoreDisplayText,
  disableAnimations,
  correctnessIndicatorStatus,
}: ScoreProps) => {
  const textPadding = Math.min(barWidth * 0.04, TEXT_MARGIN);
  const fontSize = `clamp(12px, ${barWidth * 0.3}px , 48px)`;

  const { yPos } = useSpring({
    yPos: y - height / 2 - TEXT_MARGIN,
    immediate: disableAnimations,
  });

  return (
    <Box
      data-testid="bar-score"
      as={animated.div}
      position="absolute"
      left={x + textPadding}
      width={barWidth - textPadding}
      height={height}
      style={{ top: yPos } as SpringValues}
    >
      <Box
        justifyContent="flex-end"
        height="100%"
        width="100%"
        // parent div has pointerEvents: 'none' to enable SVG behind to be clickable
        // so we need to set it to auto here for the tooltip hover to work
        style={{ pointerEvents: 'auto' }}
      >
        <Box flexDirection="row" width="100%" gap="3%" alignItems="center">
          <Tooltip
            trigger={
              <Text
                style={{
                  fontSize,
                  color: textColor,
                  fontWeight: 400,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {scoreDisplayText}
              </Text>
            }
          >
            {scoreDisplayText}
          </Tooltip>

          <CorrectnessIndicator
            shouldShow={correctnessIndicatorStatus.isVisible}
            isCorrect={correctnessIndicatorStatus.isCorrect}
            immediate={disableAnimations}
            size={fontSize}
          />
        </Box>
      </Box>
    </Box>
  );
};
